import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { sugerirEnderecos } from '../../src/lib/rotaCobranca';

/** Uma sugestão do Google Places: o id para buscar o ponto e o texto para mostrar. */
export type SugestaoEndereco = { placeId: string; text: string };

/**
 * Endereço com sugestões do Google. Espera a pessoa parar de digitar: cada
 * consulta é paga, e sem a espera "Rua 5, Setor Oeste" custaria uma por letra.
 */
const CampoEndereco: React.FC<{
    icone: React.ReactNode;
    placeholder: string;
    rotulo: string;
    valorInicial?: string;
    disabled?: boolean;
    onEscolher: (s: SugestaoEndereco) => void;
    /** Com ele, aparece o X para apagar o que está no campo. */
    onLimpar?: () => void;
}> = ({ icone, placeholder, rotulo, valorInicial = '', disabled, onEscolher, onLimpar }) => {
    const [texto, setTexto] = useState(valorInicial);
    const [sugestoes, setSugestoes] = useState<SugestaoEndereco[]>([]);
    // O texto já escolhido (ou o que veio preenchido) não é busca: sem isto ele
    // dispararia uma consulta e a lista reabriria por cima.
    const escolhidoRef = useRef<string | null>(valorInicial || null);

    useEffect(() => {
        if (texto === escolhidoRef.current) return;
        if (texto.trim().length < 3) { setSugestoes([]); return; }
        let vivo = true;
        const t = setTimeout(() => {
            sugerirEnderecos(texto)
                .then(s => { if (vivo) setSugestoes(s); })
                .catch(() => { if (vivo) setSugestoes([]); });
        }, 350);
        return () => { vivo = false; clearTimeout(t); };
    }, [texto]);

    const escolher = (s: SugestaoEndereco) => {
        escolhidoRef.current = s.text;
        setTexto(s.text);
        setSugestoes([]);
        onEscolher(s);
    };

    const limpar = () => {
        escolhidoRef.current = null;
        setTexto('');
        setSugestoes([]);
        onLimpar?.();
    };

    return (
        <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">{icone}</span>
            <input
                value={texto}
                onChange={e => setTexto(e.target.value)}
                disabled={disabled}
                placeholder={placeholder}
                aria-label={rotulo}
                className={`w-full pl-8 ${onLimpar ? 'pr-8' : 'pr-3'} py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm outline-none focus:ring-2 focus:ring-[#5F6C37] disabled:opacity-50`}
            />
            {onLimpar && texto && (
                <button onClick={limpar} disabled={disabled} aria-label={`Apagar ${rotulo.toLowerCase()}`}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                    <X size={13} />
                </button>
            )}
            {sugestoes.length > 0 && (
                <div className="absolute z-20 left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl max-h-56 overflow-y-auto">
                    {sugestoes.map(s => (
                        <button
                            key={s.placeId}
                            onClick={() => escolher(s)}
                            className="w-full text-left px-3 py-2 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-0 transition-colors"
                        >
                            {s.text}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CampoEndereco;
